import { useLocation, useNavigate } from "react-router-dom";
import { routeMap } from "../../assets/routeMap";
import { useState, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { Bell, LogOut, UserCog, AlertCircle } from "lucide-react";
import { useNotifications } from "../../hooks/useNotifications";
import { logout } from "../../slices/authSlice";
import SwitchProfileModal from "../SwitchProfileModal";

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const userDetails = useAppSelector((state) => state.auth.user);
  const authDetails = useAppSelector((state) => state.auth);
  const { unread } = useNotifications();
  const dispatch = useAppDispatch();
  const subscription = useAppSelector((state) => state.subscription.subscription);

  const [showSwitchProfileModal, setShowSwitchProfileModal] = useState<boolean>(false);
  const [showModal, setShowModal] = useState(false);

  const routeTitle = routeMap[location.pathname] || "";

  const getInitials = (name: string) =>
    name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();

  // Calculate days remaining for subscription
  const daysRemaining = useMemo(() => {
    if (!subscription?.endDate) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const expiryDate = new Date(subscription.endDate);
    expiryDate.setHours(0, 0, 0, 0);

    const diffTime = expiryDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  }, [subscription?.endDate]);

  const showSubscriptionWarning = daysRemaining !== null && daysRemaining <= 15 && daysRemaining > 0;

  return (
    <>
      {/* HEADER */}
      <header className="max-w-full z-[998] sticky top-2 border border-primary rounded-xl px-5 py-4 flex items-center justify-between backdrop-blur-xl bg-white/30">
        {/* Route Title */}
        <h1 className="text-xl font-semibold text-primary">{routeTitle}</h1>

        {/* Right Section */}
        <div className="flex items-center gap-6">

          {/* Subscription Warning */}
          {showSubscriptionWarning && (
            <button
              onClick={() => navigate("/subscription")}
              className="flex items-center gap-2 bg-orange-500 text-white px-3 py-2 rounded-md hover:bg-orange-600 transition text-sm font-medium"
            >
              <AlertCircle size={18} />
              <span className="hidden sm:inline">
                {daysRemaining === 1
                  ? "1 day remaining"
                  : `${daysRemaining} days remaining`}
              </span>
              <span className="sm:hidden">{daysRemaining}d</span>
            </button>
          )}

          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => navigate("/notifications")}
              className="relative hover:scale-110 transition bg-gray-100 rounded-full border border-primary p-2"
            >
              <Bell color="#090263" size={22} />
            </button>

            {Number(unread) > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                {unread}
              </span>
            )}
          </div>

          {/* Desktop Profile */}
          <div className="hidden md:flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center font-bold text-sm">
              {getInitials(`${userDetails?.firstname} ${userDetails?.lastname}`)}
            </div>
            <div className="text-left">
              <p className="font-medium">{userDetails?.firstname} {userDetails?.lastname}</p>
              <p className="text-sm text-gray-600">{userDetails?.email}</p>
              <div className="text-xs w-fit px-2 py-1 rounded-xl bg-blue-50 text-blue-700">{authDetails.activeRole}</div>
            </div>

          </div>

          {/* Mobile Avatar */}
          <button
            className="md:hidden w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center font-bold text-sm"
            onClick={() => setShowModal(true)}
          >
            {getInitials(`${userDetails?.firstname} ${userDetails?.lastname}`)}
          </button>
        </div>
      </header>

      {/* MOBILE PROFILE MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center md:hidden z-50">
          <div className="bg-white w-80 rounded-xl p-6 shadow-xl">
            <h2 className="text-lg font-semibold mb-4 text-primary text-center">Profile</h2>
            <div className="flex flex-col items-center gap-2">
              <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center font-bold text-lg">
                {getInitials(`${userDetails?.firstname} ${userDetails?.lastname}`)}
              </div>
              <p className="text-lg font-medium">{userDetails?.firstname} {userDetails?.lastname}</p>
              <p className="text-sm text-gray-600">{userDetails?.email}</p>
              <div className="text-xs w-fit px-2 py-1 rounded-xl bg-blue-50 text-blue-700">{authDetails.activeRole}</div>
            </div>

            <div
              onClick={() => setShowSwitchProfileModal(true)}
              className="flex items-center justify-center mt-6 gap-3 py-2 px-4 rounded-lg transition bg-primary text-white hover:bg-blue-800 delay-75 hover:cursor-pointer">
              <span><UserCog /></span>
              <span>Switch Profile</span>
            </div>

            <div
              onClick={() => {
                dispatch(logout())
                navigate("/login")
              }}
              className="flex items-center gap-3 py-3 px-4 rounded-lg transition bg-red-800 text-white hover:brightness-110 delay-75 hover:cursor-pointer">
              <span><LogOut size={22} /></span>
              <span>Sign Out</span>
            </div>


            <button
              onClick={() => setShowModal(false)}
              className="mt-2 w-full bg-black text-white py-2 rounded-lg hover:brightness-110 delay-75"
            >
              Close
            </button>
          </div>
        </div>
      )}

      <SwitchProfileModal
        isOpen={showSwitchProfileModal}
        onClose={() => setShowSwitchProfileModal(false)}
      />

    </>
  );
}
